import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Play,
  X,
  MousePointerClick,
  Briefcase,
  Smartphone,
  BellRing,
  FileText,
  Users,
} from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "@/components/site/Reveal";
import Lightbox from "@/components/site/Lightbox";

const BENEFITS = [
  {
    icon: MousePointerClick,
    title: "Simple to use",
    text: "If your team can use a phone, they can use SaleYes. No training manuals, no consultants — learn it in a day.",
  },
  {
    icon: Briefcase,
    title: "Built for business",
    text: "Every screen exists to move a number: visits, quotes, deals, dispatches. Nothing decorative, everything useful.",
  },
  {
    icon: Smartphone,
    title: "Mobile friendly",
    text: "Your business doesn't happen at a desk. SaleYes runs beautifully on the phone already in your pocket.",
  },
];

const FEATURES = [
  {
    num: "01",
    icon: BellRing,
    title: "In-app lead reminders",
    text: "Every follow-up is nudged at the right time with in-app reminders — no lead goes cold because someone forgot to call back.",
  },
  {
    num: "02",
    icon: FileText,
    title: "Stage-by-stage tracking",
    text: "From first enquiry to deal won, every lead moves through clear stages your whole team can see — and quotes go out in minutes.",
  },
  {
    num: "03",
    icon: Users,
    title: "Sales team on one page",
    text: "Visits logged from the field, daily summaries delivered automatically. Manage your sales team without the morning roll call.",
  },
];

export default function Products() {
  const [videoOpen, setVideoOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  return (
    <div data-testid="products-page">
      {/* PRODUCT HERO */}
      <section className="relative overflow-hidden pt-14 sm:pt-20 pb-24 sm:pb-28">
        <div className="absolute -left-40 top-32 w-[460px] h-[460px] rounded-full bg-brand-redSoft blur-3xl opacity-80 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal y={16}>
              <span
                data-testid="saleyes-flagship-badge"
                className="inline-flex items-center gap-2 bg-brand-redSoft text-brand-red font-mono text-xs font-semibold uppercase tracking-[0.2em] px-4 py-2 rounded-full border border-brand-red/20"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-brand-red" />
                Flagship Product
              </span>
            </Reveal>
            <h1 className="mt-6 font-display font-black tracking-tight leading-[1.02]" data-testid="saleyes-headline">
              <MaskedLine delay={0.15}>
                <img src="/saleyes-logo.png" alt="SaleYes — Sales Buddy" className="h-16 sm:h-20 lg:h-24 w-auto" />
              </MaskedLine>
              <MaskedLine delay={0.32} className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-500 mt-3">
                From lead to sale, stage by stage.
              </MaskedLine>
            </h1>
            <Reveal delay={0.5}>
              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-md" data-testid="saleyes-subtext">
                SaleYes tracks every lead stage by stage and nudges you with in-app reminders — so no enquiry, follow-up or quote ever slips through.
              </p>
            </Reveal>
            <Reveal delay={0.65}>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link
                  to="/book-demo"
                  data-testid="saleyes-book-demo-btn"
                  className="group inline-flex items-center gap-2.5 bg-brand-red hover:bg-brand-redDark text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-200 shadow-xl shadow-brand-red/25"
                >
                  Book a Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
                <button
                  onClick={() => setVideoOpen(true)}
                  data-testid="saleyes-watch-video-btn"
                  className="inline-flex items-center gap-2.5 border-2 border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-200"
                >
                  <Play className="w-4 h-4" />
                  Watch walkthrough
                </button>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.4} y={40}>
            <div className="relative">
              <div className="absolute -top-5 -right-5 w-full h-full rounded-[2rem] bg-brand-red pointer-events-none" />
              <button
                onClick={() => setLightbox({ src: "/saleyes-promo.jpg", alt: "SaleYes app poster" })}
                className="relative block w-full cursor-zoom-in rounded-[2rem]"
                data-testid="saleyes-poster-trigger"
                aria-label="View SaleYes poster full screen"
              >
                <img
                  src="/saleyes-promo.jpg"
                  alt="SaleYes app — manage customers, track leads, plan visits, monitor sales and achieve targets"
                  className="relative rounded-[2rem] w-full h-auto shadow-2xl"
                  loading="lazy"
                  data-testid="saleyes-hero-image"
                />
              </button>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl px-5 py-4 shadow-xl border border-slate-100"
                data-testid="saleyes-stat-card"
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Deals won this month</p>
                <p className="font-display font-extrabold text-2xl text-brand-ink">6,214 <span className="text-emerald-600 text-sm">+14.9%</span></p>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DELIVERYES */}
      <section className="py-24 sm:py-32 bg-slate-50" data-testid="deliveryes-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal delay={0.15} y={40}>
            <div className="relative">
              <div className="absolute -bottom-5 -left-5 w-full h-full rounded-[2rem] bg-brand-red pointer-events-none" />
              <div className="relative bg-white rounded-[2rem] border border-slate-200/70 shadow-2xl shadow-slate-900/10 overflow-hidden">
                <button
                  onClick={() => setLightbox({ src: "/deliveryes-promo.jpg", alt: "DeliverYes app poster" })}
                  className="block w-full cursor-zoom-in"
                  data-testid="deliveryes-poster-trigger"
                  aria-label="View DeliverYes poster full screen"
                >
                  <img
                    src="/deliveryes-promo.jpg"
                    alt="DeliverYes app — purchase order, customer details, plan and schedule, dispatch, live delivery updates, reports and insights"
                    className="w-full h-auto"
                    loading="lazy"
                    data-testid="deliveryes-promo-image"
                  />
                </button>
              </div>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                className="absolute -top-6 -right-4 sm:-right-8 bg-brand-ink text-white rounded-2xl px-5 py-4 shadow-xl"
                data-testid="deliveryes-stat-card"
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">PO to delivery</p>
                <p className="font-display font-extrabold text-xl">In a few clicks</p>
              </motion.div>
            </div>
          </Reveal>
          <div>
            <Reveal y={16}>
              <span
                data-testid="deliveryes-badge"
                className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 font-mono text-xs font-semibold uppercase tracking-[0.2em] px-4 py-2 rounded-full border border-emerald-200"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Delivery Made Easy
              </span>
            </Reveal>
            <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="deliveryes-headline">
              Purchase order to delivery — made easy.
            </h2>
            <Reveal delay={0.3}>
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-md" data-testid="deliveryes-subtext">
                DeliverYes takes you from purchase order to dispatch and delivery details in a few clicks — no spreadsheets, no phone chains, no guesswork.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <ul className="mt-8 space-y-4">
                {[
                  "Convert purchase orders into dispatches in a few clicks",
                  "Live delivery status for every order, shared with customers",
                  "Delivery details and proof captured on the spot",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-red flex items-center justify-center shrink-0">
                      <ArrowRight className="w-3 h-3 text-white" />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.5}>
              <Link
                to="/book-demo"
                data-testid="deliveryes-book-demo-btn"
                className="group mt-10 inline-flex items-center gap-2.5 bg-brand-red hover:bg-brand-redDark text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-200 shadow-xl shadow-brand-red/25"
              >
                Book a Demo
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VIDEO WALKTHROUGH */}
      <section className="bg-brand-ink py-24 sm:py-32" data-testid="video-walkthrough-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center">
              <Eyebrow dark>Product Walkthrough</Eyebrow>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
                See SaleYes in action.
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <button
              onClick={() => setVideoOpen(true)}
              data-testid="product-video-placeholder"
              className="group mt-12 relative w-full aspect-video bg-brand-inkCard border border-slate-700 rounded-[2rem] overflow-hidden block"
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
                <span className="relative flex items-center justify-center">
                  <span className="absolute w-24 h-24 rounded-full bg-brand-red/30 animate-ping" />
                  <span className="relative w-20 h-20 rounded-full bg-brand-red group-hover:bg-brand-redDark flex items-center justify-center transition-colors duration-200 shadow-2xl shadow-brand-red/40">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </span>
                </span>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">Walkthrough video — coming soon</p>
              </div>
              <div aria-hidden className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 sm:py-32" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Why SaleYes</Eyebrow>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
              Three promises. Zero fine print.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.1}>
                <div
                  data-testid={`benefit-card-${i}`}
                  className="group bg-white border border-slate-200/80 rounded-3xl p-8 h-full hover:shadow-xl hover:-translate-y-1 hover:border-brand-red/30 transition-shadow transition-transform duration-300"
                >
                  <span className="w-12 h-12 rounded-2xl bg-brand-redSoft text-brand-red flex items-center justify-center group-hover:bg-brand-red group-hover:text-white transition-colors duration-300">
                    <b.icon className="w-6 h-6" />
                  </span>
                  <h3 className="mt-6 font-display font-bold text-xl tracking-tight">{b.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE DEEP DIVE */}
      <section className="py-24 sm:py-32 bg-slate-50" data-testid="feature-deep-dive">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Under the hood</Eyebrow>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
              Everything your field day needs.
            </h2>
          </Reveal>
          <div className="mt-14 space-y-4">
            {FEATURES.map((f, i) => (
              <Reveal key={f.num} delay={i * 0.08}>
                <div
                  data-testid={`feature-row-${f.num}`}
                  className="group grid sm:grid-cols-[auto_auto_1fr] items-start gap-5 sm:gap-8 bg-white border border-slate-200/80 rounded-3xl p-7 sm:p-9 hover:border-brand-red/40 hover:shadow-lg transition-shadow transition-colors duration-300"
                >
                  <p className="font-mono text-brand-red font-semibold text-sm tracking-widest pt-1">{f.num}</p>
                  <span className="w-12 h-12 rounded-2xl bg-brand-ink text-white flex items-center justify-center shrink-0 group-hover:bg-brand-red transition-colors duration-300">
                    <f.icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{f.title}</h3>
                    <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed max-w-2xl">{f.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 px-5 sm:px-8" data-testid="products-cta-banner">
        <Reveal>
          <div className="max-w-7xl mx-auto bg-brand-ink rounded-[2.5rem] px-8 sm:px-14 py-16 sm:py-20 relative overflow-hidden">
            <div aria-hidden className="absolute -left-10 -bottom-28 font-display font-black text-[11rem] leading-none text-white/5 select-none pointer-events-none">
              YES
            </div>
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div>
                <h2 className="font-display font-extrabold text-white tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Put <span className="inline-flex items-center bg-white rounded-full px-4 py-1 align-middle"><img src="/saleyes-logo.png" alt="SaleYes" className="h-8 sm:h-10 w-auto" /></span> on your next dispatch.
                </h2>
                <p className="mt-4 text-slate-400 max-w-md text-sm sm:text-base leading-relaxed">
                  A 30-minute demo on your own routes and quotes. See exactly what changes on day one.
                </p>
              </div>
              <Link
                to="/book-demo"
                data-testid="products-cta-book-demo-btn"
                className="group inline-flex items-center justify-center gap-2.5 bg-brand-blue hover:bg-brand-blueDark text-white font-bold px-8 py-4 rounded-full transition-colors duration-200 shrink-0"
              >
                Book a Demo
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <Lightbox src={lightbox?.src} alt={lightbox?.alt || ""} onClose={() => setLightbox(null)} />

      {/* VIDEO MODAL */}
      <AnimatePresence>
        {videoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-brand-ink/80 backdrop-blur-sm flex items-center justify-center p-5"
            onClick={() => setVideoOpen(false)}
            data-testid="video-modal"
          >
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 20 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-white rounded-3xl max-w-lg w-full p-10 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setVideoOpen(false)}
                data-testid="video-modal-close"
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors duration-200"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
              <span className="mx-auto w-16 h-16 rounded-full bg-brand-redSoft text-brand-red flex items-center justify-center">
                <Play className="w-6 h-6 ml-0.5" />
              </span>
              <h3 className="mt-6 font-display font-bold text-2xl tracking-tight">Walkthrough coming soon</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                We're putting the finishing touches on the SaleYes product video. Book a demo and we'll walk you through the real thing, live.
              </p>
              <Link
                to="/book-demo"
                data-testid="video-modal-book-demo-btn"
                className="mt-7 inline-flex items-center gap-2 bg-brand-red hover:bg-brand-redDark text-white font-bold px-6 py-3 rounded-full transition-colors duration-200"
              >
                Book a live demo
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
